//! Session memory of which cells this tab just imported from which document.
//!
//! Sources live in Postgres and are read back per cell by the cell-history
//! endpoint, but they only land there after checkpoint. This store covers the
//! gap: a cell imported seconds ago has a source here, so the cell-history panel
//! can show it immediately instead of minutes later.
//!
//! Structural edits are applied locally for the same reason. The server shifts
//! its own rows during ingest, but until then an inserted row would leave
//! freshly imported cells pointing at their old neighbours' sources.

import { events } from '@/app/events/events';
import type { CellDocumentSource, CellDocumentSourceStamp } from 'quadratic-shared/typesAndSchemasDocuments';

/** The axis a row/column insert or delete shifts cells along. */
export type ShiftAxis = 'x' | 'y';

export interface CellSourceShift {
  sheetId: string;
  axis: ShiftAxis;
  /** First affected column/row. */
  at: number;
  /** `+count` for an insert, `-count` for a delete. */
  delta: number;
  /** Deletes only: drop the sources in the removed lines first. */
  deleteAt?: boolean;
}

const key = (x: number, y: number): string => `${x},${y}`;

/**
 * Stamps carry no `updatedAt` from the server and are always treated as
 * available: the drive document was read moments ago.
 */
const stampToSource = (stamp: CellDocumentSourceStamp): CellDocumentSource => ({
  ...stamp,
  available: true,
  updatedAt: new Date().toISOString(),
});

class CellDocumentSources {
  /** sheetId → `x,y` → source, for sources stamped in this session. */
  private stamped = new Map<string, Map<string, CellDocumentSource>>();

  constructor() {
    // A grid load means a different file (or a reload of this one) now owns the
    // sheets, so stamps from the previous grid must not be attributed to it.
    events.on('sheetsInfo', this.reset);
  }

  reset = () => {
    this.stamped.clear();
  };

  get(sheetId: string, x: number, y: number): CellDocumentSource | undefined {
    return this.stamped.get(sheetId)?.get(key(x, y));
  }

  /** Every source stamped in this session for a sheet. */
  forSheet(sheetId: string): CellDocumentSource[] {
    return Array.from(this.stamped.get(sheetId)?.values() ?? []);
  }

  /** Record stamps the client just sent on a transaction. */
  add(stamps: CellDocumentSourceStamp[]) {
    for (const stamp of stamps) {
      const sheet = this.stamped.get(stamp.sheetId) ?? new Map();
      sheet.set(key(stamp.x, stamp.y), stampToSource(stamp));
      this.stamped.set(stamp.sheetId, sheet);
    }
  }

  clear(positions: { sheetId: string; x: number; y: number }[]) {
    for (const position of positions) {
      this.stamped.get(position.sheetId)?.delete(key(position.x, position.y));
    }
  }

  /** Move sources along with the cells a row/column insert or delete shifted. */
  shift(shift: CellSourceShift) {
    const sheet = this.stamped.get(shift.sheetId);
    if (!sheet || sheet.size === 0) return;

    const shifted = new Map<string, CellDocumentSource>();
    for (const source of sheet.values()) {
      const coord = shift.axis === 'x' ? source.x : source.y;
      if (shift.deleteAt && coord >= shift.at && coord < shift.at - shift.delta) continue;
      if (coord < shift.at) {
        shifted.set(key(source.x, source.y), source);
        continue;
      }
      const moved =
        shift.axis === 'x' ? { ...source, x: source.x + shift.delta } : { ...source, y: source.y + shift.delta };
      shifted.set(key(moved.x, moved.y), moved);
    }
    this.stamped.set(shift.sheetId, shifted);
  }
}

export const cellDocumentSources = new CellDocumentSources();
